import { useLayoutEffect, useState, RefObject } from "react";
import { ObserverOptions } from "./types";

let intersectionObserver: IntersectionObserver;

const intersectionCallbacks: WeakMap<
  Element,
  React.Dispatch<React.SetStateAction<boolean>>
> = new WeakMap();

const handleIntersections = (entires: IntersectionObserverEntry[]) => {
  entires.forEach(entry => {
    if (intersectionCallbacks.has(entry.target)) {
      const callback = intersectionCallbacks.get(entry.target);

      callback?.(entry.isIntersecting);
    }
  });
};

const intersectionObserverInit = (options?: ObserverOptions) => {
  if (intersectionObserver) {
    return;
  }

  intersectionObserver = new IntersectionObserver(handleIntersections, options);
};

// options aren't memoized to allow effect run in case if caller implements
// conditional rendering and ref value will be assigned on secondary render

export const useIntersectionObserver = (
  ref: RefObject<Element>,
  options?: ObserverOptions,
) => {
  const [isIntersecting, setIsIntersecting] = useState(true);

  useLayoutEffect(() => {
    const { current: element } = ref;

    if (element) {
      const newOptions = { ...options };

      if (!newOptions?.rootMargin) {
        newOptions.rootMargin = `-0px 0px 0px 0px`;
      }

      intersectionObserverInit(newOptions);

      intersectionCallbacks.set(element, setIsIntersecting);
      intersectionObserver.observe(element);
    }
  }, [ref, options]);

  useLayoutEffect(() => {
    const { current: element } = ref;

    return () => {
      if (element) {
        intersectionCallbacks.delete(element);
        intersectionObserver.unobserve(element);
      }
    };
  }, [ref]);

  return isIntersecting;
};
